// ============================================================================
// Motor do LOTE CONTÁBIL SAP do Petty Cash (funções puras)
// ----------------------------------------------------------------------------
// Separado do componente React para permitir TESTE UNITÁRIO — é lógica de
// dinheiro (o lote que a Contabilidade lança no SAP), que a diretriz do projeto
// manda testar. O componente `FinanceiroPettyCashRelatorio.js` só desenha o
// resultado destas funções.
//
// COMO O LOTE É MONTADO (por loja):
//   1. UMA linha de CRÉDITO na conta fonte (11110100000004 — PETTY CASH), com o
//      total que saiu do fundo no período;
//   2. UMA linha por movimento, na conta de despesa dele.
//
// A falta/sobra (linha TIPO='AJUSTE', criada na tela "Não Validado") entra no
// lote desde 2026-07-24. Duas decisões do card que NÃO se adivinham lendo o
// código:
//
//   • CONTA — a falta/sobra vai na MESMA `CONTA_DESPESA` do lançamento de
//     origem (não existe conta de quebra de caixa no plano de contas do grupo).
//     Consequência aceita: a sobra vira um crédito numa conta de despesa
//     (estorno parcial) e a falta engorda a despesa; a divergência de caixa não
//     aparece isolada na contabilidade, só no portal.
//   • LADO — o sinal do VALOR_COMPUTADO decide: falta (+) DEBITA, sobra (−)
//     CREDITA. Nunca sai valor negativo no CSV — o SAP não aceita.
//
// O lote fecha sozinho (Σ débitos = Σ créditos) porque a linha do PETTY CASH
// soma os mesmos valores assinados que viram as linhas de contrapartida.
// ============================================================================

// Conta fonte do fundo fixo — de onde o dinheiro sai (e para onde volta, quando
// a sobra é maior que a despesa do período).
export const CONTA_PETTY_CASH = '11110100000004';
export const NOME_CONTA_PETTY_CASH = 'PETTY CASH';

// ── Regra de distribuição (coluna "Regra distr." do CSV) ────────────────────
// Por padrão é o próprio código da loja. PCI e PCS são a exceção (2026-08-06):
// no SAP elas se distribuem pela EMPRESA, não pela loja.
// ⚠️ Este mapa NÃO reaproveita o `src/utils/empresas.js` de propósito: lá a
// empresa da PCS se chama 'GC FOOD', e o que o SAP espera nesta coluna é 'GC'.
// São vocabulários diferentes para a mesma entidade — juntar os dois faria uma
// mudança de rótulo em qualquer tela do portal vazar para o lote contábil.
// ⚠️ A coluna "Filial" NÃO acompanha: ela continua identificando a loja física,
// que é o que ela significa em todas as outras lojas do lote.
export const REGRA_DISTRIBUICAO_POR_LOJA = {
    PCI: 'P&M',
    PCS: 'GC',
};

// Loja sem exceção cadastrada distribui por ela mesma — é o comportamento de
// sempre, e o que mantém as outras 11 lojas inalteradas.
export const regraDistribuicao = (loja) => REGRA_DISTRIBUICAO_POR_LOJA[loja] || loja;

// Arredonda para centavos. Existe por causa do float: uma soma de 599,99 pode
// virar 599,9999999 e estragar a comparação de paridade do lote.
const emCentavos = (valor) => Math.round((Number(valor) || 0) * 100) / 100;

// Relatório e SAP acompanham o CAIXA: valor computado + competência (decisões do
// Financeiro no "Não Validado"), senão o limite divergiria da contabilidade.
// Os fallbacks para o original cobrem a janela entre migration e deploy.
export const mesDoLancamento = (t) => t.COMPETENCIA || t.DATA_COMPRA.substring(3);
export const valorDoLancamento = (t) => parseFloat(t.VALOR_COMPUTADO ?? t.VALOR);

// Um movimento é falta/sobra quando veio como linha de ajuste do "Não Validado".
const ehAjuste = (t) => t.TIPO === 'AJUSTE';

// Monta as linhas do lote a partir dos movimentos JÁ FILTRADOS (período/loja) e
// já restritos a STATUS='Aprovado' — só entra no SAP o que foi reposto no caixa.
// Devolve a lista pronta para a tabela, o CSV e o POST /sap-lote/salvar.
export function gerarLinhasSAP(movimentos) {
    const linhas = [];
    const lojas = [...new Set(movimentos.map(m => m.LOJA))];
    let numeroLinha = 1;

    lojas.forEach(loja => {
        const daLoja = movimentos.filter(m => m.LOJA === loja);
        // Soma ASSINADA: a sobra (valor negativo) reduz o que saiu do fundo, a
        // falta aumenta. É o que faz o lote fechar sem conta de ajuste.
        const totalLoja = emCentavos(daLoja.reduce((acc, m) => acc + valorDoLancamento(m), 0));

        // 1. Linha da conta fonte (PETTY CASH).
        // Normalmente é CRÉDITO (dinheiro saiu do fundo). Se o total ficar
        // negativo — período em que a sobra supera as despesas, possível porque o
        // ajuste tem competência própria e pode cair sozinho num mês —, a linha
        // vira DÉBITO com o valor absoluto: dinheiro VOLTANDO para o fundo. Sem
        // essa inversão sairia um crédito negativo no CSV.
        // Total exatamente zero (falta/sobra que anula a despesa) não gera linha:
        // nada saiu nem voltou, e uma linha de R$ 0,00 só polui o lote.
        if (totalLoja !== 0) {
            const voltouParaOFundo = totalLoja < 0;
            linhas.push({
                linha: numeroLinha++,
                tipoLinha: voltouParaOFundo ? 'DEBITO' : 'CREDITO',
                transacaoId: null,
                conta: CONTA_PETTY_CASH,
                nomeConta: NOME_CONTA_PETTY_CASH,
                debito: voltouParaOFundo ? Math.abs(totalLoja) : null,
                credito: voltouParaOFundo ? null : totalLoja,
                observacoes: 'Contas a pagar - Reposição',
                regraDistr: '',
                filial: loja
            });
        }

        // 2. Contrapartidas — uma linha por movimento, na conta de despesa dele
        //    (a linha de ajuste já nasce herdando a CONTA_DESPESA do pai).
        daLoja.forEach(m => {
            const valor = emCentavos(valorDoLancamento(m));
            const ehCredito = valor < 0; // sobra: estorna a despesa
            linhas.push({
                linha: numeroLinha++,
                tipoLinha: ehCredito ? 'CREDITO' : 'DEBITO',
                transacaoId: m.ID,
                conta: m.CONTA_DESPESA,
                nomeConta: m.DESCRICAO,
                debito: ehCredito ? null : valor,
                credito: ehCredito ? Math.abs(valor) : null,
                observacoes: ehAjuste(m)
                    ? `Contas a pagar / Falta-sobra ref. lançamento #${m.AJUSTE_ORIGEM_ID}`
                    : `Contas a pagar / Cupom: ${m.NUMERO_CUPOM || 'S/N'}`,
                regraDistr: regraDistribuicao(loja),
                filial: loja
            });
        });
    });

    return linhas;
}

// Totais do lote — usado no teste de paridade (e disponível para a tela, se um
// dia quisermos mostrar o rodapé de conferência).
export function totaisDoLote(linhas) {
    const debito = emCentavos(linhas.reduce((acc, l) => acc + (l.debito || 0), 0));
    const credito = emCentavos(linhas.reduce((acc, l) => acc + (l.credito || 0), 0));
    return { debito, credito, bate: debito === credito };
}
